import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const DEFAULT_PREFS = {
  theme: { accent_color: '#10B981', sidebar_position: 'left' },
  sidebar_order: [
    '/app', '/app/recherche', '/app/etude', '/app/visites', '/app/offres',
    '/app/pipeline', '/app/travaux', '/app/bordereau', '/app/bons-commande',
    '/app/dossier-financement', '/app/calendrier', '/app/reseau', '/app/documents'
  ],
  dashboard_widgets: [
    { id: 'stats', visible: true, label: 'Statistiques' },
    { id: 'pipeline_chart', visible: true, label: 'Pipeline' },
    { id: 'pie_chart', visible: true, label: 'Repartition' },
    { id: 'recent_projects', visible: true, label: 'Projets recents' },
  ],
};

const PreferencesContext = createContext({
  prefs: DEFAULT_PREFS,
  setPrefs: () => {},
  savePrefs: async () => {},
  loading: true,
});

export const PreferencesProvider = ({ children }) => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/preferences`, { withCredentials: true });
        const loaded = { ...DEFAULT_PREFS, ...res.data };
        // Migration : anciennes URLs (sans /app) → nouvelles (/app/...)
        const legacyMap = {
          '/': '/app',
          '/recherche': '/app/recherche',
          '/etude': '/app/etude',
          '/visites': '/app/visites',
          '/offres': '/app/offres',
          '/pipeline': '/app/pipeline',
          '/travaux': '/app/travaux',
          '/bordereau': '/app/bordereau',
          '/bons-commande': '/app/bons-commande',
          '/calendrier': '/app/calendrier',
          '/reseau': '/app/reseau',
          '/documents': '/app/documents',
          '/artisans': '/app/reseau',
          '/alertes': '/app/recherche',
        };
        loaded.sidebar_order = (loaded.sidebar_order || []).map(p => legacyMap[p] || p);
        // Backfill : ajoute les nouveaux items (ex. dossier-financement) à la fin
        const missing = DEFAULT_PREFS.sidebar_order.filter(p => !loaded.sidebar_order.includes(p));
        loaded.sidebar_order = [...loaded.sidebar_order, ...missing];
        // Déduplication
        loaded.sidebar_order = loaded.sidebar_order.filter((p, i, arr) => arr.indexOf(p) === i);
        setPrefs(loaded);
      } catch {
        setPrefs(DEFAULT_PREFS);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // Apply accent color to CSS variables whenever it changes
  useEffect(() => {
    const accent = prefs?.theme?.accent_color || '#10B981';
    document.documentElement.style.setProperty('--accent-color', accent);
    document.documentElement.style.setProperty('--accent-color-15', accent + '26');
    document.documentElement.style.setProperty('--accent-color-30', accent + '4D');
  }, [prefs?.theme?.accent_color]);

  const savePrefs = useCallback(async (next) => {
    const payload = next || prefs;
    setPrefs(payload);
    await axios.put(`${API}/preferences`, payload, { withCredentials: true });
  }, [prefs]);

  return (
    <PreferencesContext.Provider value={{ prefs, setPrefs, savePrefs, loading }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => useContext(PreferencesContext);
